import { useState } from "react";
import { ShieldAlert, Loader2, Database, CheckCircle2, XCircle, Lock } from "lucide-react";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const METRICS = [
  ["Active Storage Provider", "active_storage_provider"],
  ["Production Media Records", "production_media_records"],
  ["Production Referenced Objects", "referenced_objects"],
  ["Total Bytes", "total_bytes"],
  ["Total MB", "total_mb"],
  ["Tenants", "tenants"],
  ["Locations", "locations"],
  ["Source Objects Present", "source_objects_present"],
  ["Source Objects Missing", "source_objects_missing"],
  ["Duplicate Paths", "duplicate_paths"],
  ["Unattached Media Records (info)", "unattached_media_records"],
  ["Zero-Byte Objects", "zero_byte_objects"],
  ["Unknown Items", "unknown_items"],
  ["Cross-Tenant Issues", "cross_tenant_issues"],
  ["Cross-Location Issues", "cross_location_issues"],
  ["Demo/QA/Test Items Found", "demo_qa_test_items_found"],
  ["Pending Deletes Excluded", "pending_deletes_excluded"],
  ["Current R2 Production Object Count", "current_r2_production_object_count"],
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.is_platform_admin;
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    // exactly one call; no auto retry, no polling, no second run after success
    if (state === "loading" || state === "done") return;
    setState("loading");
    setError("");
    try {
      const { data } = await api.post("/system-tests/production-media-inventory-admin");
      setReport(data);
      setState("done");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Inventar fehlgeschlagen.");
      setState("error");
    }
  };

  return (
    <CockpitLayout active="super-admin" title="System-Admin">
      {!isPlatformAdmin ? (
        <div className="mx-auto max-w-md py-16 text-center" data-testid="super-admin-no-access">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
            <Lock className="text-red-500" />
          </span>
          <h1 className="mt-4 text-[22px] font-black text-fundio-ink">Kein Zugriff</h1>
          <p className="mt-2 text-sm text-fundio-slate">
            Dieser Bereich ist ausschliesslich für den FUNDIO Platform-Super-Admin.
          </p>
        </div>
      ) : (
        <div className="mx-auto max-w-3xl" data-testid="super-admin-panel">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-fundio-gold/10">
              <ShieldAlert className="text-fundio-gold" size={22} />
            </span>
            <div>
              <h1 className="text-[22px] font-black text-fundio-ink">Production Media Inventory</h1>
              <p className="text-[13px] text-fundio-slate">Nur Platform-Super-Admin · P1-C3E Migrations-Vorbereitung</p>
            </div>
          </div>

          {/* READ ONLY warning */}
          <div
            className="mt-5 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
            data-testid="super-admin-readonly-warning"
          >
            <ShieldAlert className="mt-0.5 shrink-0 text-amber-600" size={18} />
            <div className="text-[13px] leading-relaxed text-amber-900">
              <strong className="font-black uppercase tracking-wide">READ ONLY – KEIN CUTOVER.</strong>{" "}
              Dieser Lauf liest nur Metadaten (Anzahl, Bytes, Pfade). Es werden{" "}
              <strong>keine Daten kopiert, gelöscht, migriert</strong> oder umgeschaltet.
            </div>
          </div>

          {/* Trigger */}
          <div className="mt-5 rounded-2xl border border-fundio-border bg-white p-5">
            <button
              type="button"
              onClick={run}
              disabled={state === "loading" || state === "done"}
              data-testid="super-admin-run-inventory-btn"
              className="inline-flex items-center gap-2.5 rounded-xl bg-fundio-gold px-5 py-3 text-[14px] font-bold text-fundio-black transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            >
              {state === "loading" ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Database size={18} />
              )}
              Production Media Inventory – Read Only
            </button>
            {state === "done" && (
              <p className="mt-3 text-[12.5px] font-medium text-status-success" data-testid="super-admin-run-complete">
                Lauf abgeschlossen. Der Button ist deaktiviert – für einen erneuten Lauf die Seite neu laden.
              </p>
            )}
            {state === "error" && (
              <p className="mt-3 flex items-center gap-2 text-[12.5px] font-medium text-red-600" data-testid="super-admin-run-error">
                <XCircle size={15} /> {error}
              </p>
            )}
          </div>

          {/* Result */}
          {state === "done" && report && (
            <div className="mt-5 space-y-5" data-testid="super-admin-report">
              <div
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                  report.safe_for_copy_phase
                    ? "border-status-success-border bg-status-success-bg text-status-success"
                    : "border-amber-300 bg-amber-50 text-amber-800"
                }`}
                data-testid="super-admin-safe-badge"
              >
                {report.safe_for_copy_phase ? <CheckCircle2 size={18} /> : <ShieldAlert size={18} />}
                <span className="text-[14px] font-black">
                  SAFE FOR COPY PHASE: {report.safe_for_copy_phase ? "YES" : "NO"}
                </span>
              </div>

              <div className="overflow-hidden rounded-2xl border border-fundio-border bg-white">
                <dl className="divide-y divide-fundio-border">
                  {METRICS.map(([label, key]) => (
                    <div key={key} className="flex items-center justify-between px-4 py-2.5" data-testid={`metric-${key}`}>
                      <dt className="text-[13px] text-fundio-slate">{label}</dt>
                      <dd className="font-mono text-[13px] font-semibold text-fundio-ink">{String(report[key])}</dd>
                    </div>
                  ))}
                  <div className="flex items-center justify-between bg-fundio-subtle px-4 py-2.5">
                    <dt className="text-[13px] font-bold text-fundio-ink">Migration Scope</dt>
                    <dd className="font-mono text-[12.5px] text-fundio-ink">
                      {report.migration_scope?.media_records} records · {report.migration_scope?.object_paths} paths ·{" "}
                      {report.migration_scope?.total_bytes} bytes
                    </dd>
                  </div>
                </dl>
              </div>

              <details className="rounded-2xl border border-fundio-border bg-white p-4" data-testid="super-admin-raw-json">
                <summary className="cursor-pointer text-[13px] font-semibold text-fundio-slate">Raw JSON</summary>
                <pre className="mt-3 max-h-96 overflow-auto rounded-lg bg-fundio-dark p-4 text-[11.5px] leading-relaxed text-fundio-cream">
{JSON.stringify(report, null, 2)}
                </pre>
              </details>

              <p className="text-[12px] text-fundio-slate">
                Customer data modified: NO · Files copied: NO · Files deleted: NO · Storage cutover performed: NO
              </p>
            </div>
          )}
        </div>
      )}
    </CockpitLayout>
  );
}
